"use client";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Star, Package, Truck, Loader2 } from "lucide-react";
import { formatCurrency } from "@/utils/currency";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  getShipmentProvidersForOrderSet,
} from "@/services/orderService";

interface ShippingProvider {
  id: string;
  partnerName: string;
  rating: number;
  deliveryCharge: number;
  shipmentType: "AIR" | "SURFACE";
  rtoCost: number;
  expectedPickup: string;
  expectedDelivery: string;
  chargeableWeight: number;
  serviceName?: string;
  minWeight?: number;
  maxWeight?: number;
  zones?: string[];
  // New detailed fields from updated API
  correlationId?: string;
  courierOptionId?: string;
  merchantId?: string;
  carrierPartnerId?: string;
  zoneId?: string;
  zoneName?: string;
  chargeableWeightDetails?: any;
  shipmentOrderType?: string;
  orderType?: string;
  pickupPostalCode?: string;
  dropPostalCode?: string;
  forwardBasePrice?: number;
  fuelSurchargePercentageOfBasePrice?: number;
  fuelSurchargeAmount?: number;
  rtoChargePercentOfForwardCharge?: number;
  totalAmountBeforeTax?: number;
  taxPercentage?: number;
  totalTaxAmount?: number;
  totalAmountAfterTax?: number;
  currencyCode?: string;
  minTatInDays?: number;
  maxTatInDays?: number;
  raw?: any;
}

interface ShippingProviderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectProvider: (provider: ShippingProvider) => void;
  providers?: ShippingProvider[];
  storeId?: string;
  orderId?: string;
  orderSetId?: string;
  shipmentProviderMode?: "seller" | "legacy";
  destinationPincode?: string;
  packageWeight?: number;
  pickupPincode?: string;
  paymentMode?: "PREPAID" | "POSTPAID" | "SEMIPREPAID";
  price?: number;
  lengthInCm?: number;
  breadthInCm?: number;
  heightInCm?: number;
}

const ShippingProviderModal = ({
  isOpen,
  onClose,
  onSelectProvider,
  providers: propProviders,
  storeId,
  orderId,
  orderSetId,
  shipmentProviderMode,
  destinationPincode,
  packageWeight = 0.5,
  pickupPincode,
  paymentMode: propPaymentMode,
  price = 0,
  lengthInCm,
  breadthInCm,
  heightInCm,
}: ShippingProviderModalProps) => {
  const [providers, setProviders] = useState<ShippingProvider[]>(
    propProviders || [],
  );
  const [loading, setLoading] = useState(false);
  const [selectedPaymentMode, setSelectedPaymentMode] = useState<
    "PREPAID" | "POSTPAID" | "SEMIPREPAID"
  >(propPaymentMode || "PREPAID");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const resolvedMode = useMemo(() => {
    if (shipmentProviderMode) return shipmentProviderMode;
    if (orderId && orderSetId) return "seller";
    return "legacy";
  }, [shipmentProviderMode, orderId, orderSetId]);

  const modeLabel = useMemo(() => {
    switch (resolvedMode) {
      case "seller":
        return "Seller order set";
      default:
        return "Standalone dispatch";
    }
  }, [resolvedMode]);

  const toggleDetails = (id: string) => {
    setExpanded((prev) => {
      const clone = new Set(prev);
      if (clone.has(id)) clone.delete(id);
      else clone.add(id);
      return clone;
    });
  };

  // Fetch shipping providers from API when modal opens
  const fetchShippingProviders = useCallback(async () => {
    if (!storeId) return;

    setLoading(true);
    try {
      let resp;

      if (resolvedMode === "seller") {
        if (!orderId || !orderSetId) {
          toast.error(
            "Order and order set are required to load shipping providers",
          );
          return;
        }
        resp = await getShipmentProvidersForOrderSet(orderId, orderSetId);
      } else {
        // Legacy fallback for standalone dispatch flow.
        const weightInGrams = Math.round((packageWeight || 0) * 1000);
        const payment_mode = selectedPaymentMode;
        const length_in_cm = lengthInCm || 10;
        const breadth_in_cm = breadthInCm || 10;
        const height_in_cm = heightInCm || 10;

        const body = {
          pickup_pincode: pickupPincode || "",
          delivery_pincode: destinationPincode || "",
          weight_in_grams: weightInGrams,
          price: price || 0,
          length_in_cm,
          breadth_in_cm,
          height_in_cm,
          payment_mode,
        };

        const { apiClient } = await import("@/lib/apiClient");
        resp = await apiClient.post(
          `/api/v1/admin/:storeId/order/courier-options`,
          body,
        );
      }

      if (resp && resp.valid && resp.data) {
        const transformedProviders: ShippingProvider[] = (
          resp.data as any[]
        ).map((provider: any) => {
          if (provider && provider.carrier_partner_id) {
            const cw = provider.chargeable_weight_details || {};
            const chargeableWeightInGrams =
              cw.chargeable_weight_in_grams ??
              cw.dead_weight_in_grams ??
              cw.volumetric_weight_in_grams ??
              null;

            const chargeableWeight = chargeableWeightInGrams
              ? Math.round(chargeableWeightInGrams) / 1000
              : packageWeight;

            const deliveryCharge =
              provider.total_amount_charged_after_tax ??
              provider.total_amount_charged_before_tax ??
              provider.forward_base_price ??
              0;

            const rtoPercent =
              provider.rto_charge_percent_of_forward_charge ?? 0;
            const rtoCost =
              rtoPercent && provider.forward_base_price
                ? (rtoPercent / 100) * provider.forward_base_price
                : 0;

            return {
              id: provider.carrier_partner_id,
              partnerName:
                provider.carrier_partner_public_name ||
                provider.merchant_id ||
                provider.partner_name ||
                "Unknown Partner",
              rating: provider.rating || 4.0,
              deliveryCharge,
              shipmentType:
                provider.shipment_order_type === "AIR" ? "AIR" : "SURFACE",
              rtoCost,
              expectedPickup: provider.min_tat_in_days
                ? `${provider.min_tat_in_days} days`
                : "",
              expectedDelivery: provider.max_tat_in_days
                ? `${provider.max_tat_in_days} days`
                : "",
              chargeableWeight,
              serviceName:
                provider.carrier_partner_public_name || provider.service_name,
              minWeight: undefined,
              maxWeight: undefined,
              zones: provider.zone_name ? [provider.zone_name] : provider.zones,
              correlationId: provider.correlation_id,
              courierOptionId: provider.correlation_id,
              merchantId: provider.merchant_id,
              carrierPartnerId: provider.carrier_partner_id,
              zoneId: provider.zone_id,
              zoneName: provider.zone_name,
              chargeableWeightDetails: provider.chargeable_weight_details,
              shipmentOrderType: provider.shipment_order_type,
              orderType: provider.order_type,
              pickupPostalCode: provider.pickup_postal_code,
              dropPostalCode: provider.drop_postal_code,
              forwardBasePrice: provider.forward_base_price,
              fuelSurchargePercentageOfBasePrice:
                provider.fuel_surcharge_percentage_of_base_price,
              fuelSurchargeAmount: provider.fuel_surcharge_amount,
              rtoChargePercentOfForwardCharge:
                provider.rto_charge_percent_of_forward_charge,
              totalAmountBeforeTax: provider.total_amount_charged_before_tax,
              taxPercentage: provider.tax_percentage,
              totalTaxAmount: provider.total_tax_amount,
              totalAmountAfterTax: provider.total_amount_charged_after_tax,
              currencyCode: provider.currency_code,
              minTatInDays: provider.min_tat_in_days,
              maxTatInDays: provider.max_tat_in_days,
              raw: provider,
            } as ShippingProvider;
          }

          return {
            id:
              provider.courier_code ||
              provider.id ||
              provider.partner_name ||
              provider.name,
            partnerName:
              provider.courier_name ||
              provider.partner_name ||
              provider.name ||
              "Unknown Partner",
            rating: provider.rating || 4.0,
            deliveryCharge:
              provider.total_freight ??
              provider.delivery_charge ??
              provider.cost ??
              0,
            shipmentType:
              provider.shipment_type ||
              (provider.freight_mode === "AIR" ? "AIR" : "SURFACE"),
            rtoCost: provider.rto_cost || provider.return_cost || 0,
            expectedPickup:
              provider.expected_pickup || provider.expected_pick || "",
            expectedDelivery: provider.expected_delivery || "",
            chargeableWeight: provider.chargeable_weight || packageWeight,
            serviceName: provider.freight_mode || provider.service_name,
            minWeight: provider.min_weight || provider.min_weight_grams,
            maxWeight: provider.max_weight || provider.max_weight_grams,
            zones: provider.zones,
          } as ShippingProvider;
        });

        setProviders(transformedProviders);
      } else {
        toast.error("Failed to load shipping providers");
      }
    } catch (error) {
      console.error("Error fetching shipping providers:", error);
      toast.error("Failed to load shipping providers");

      setProviders([
        {
          id: "fallback-1",
          partnerName: "Standard Delivery",
          rating: 4.0,
          deliveryCharge: 50,
          shipmentType: "SURFACE",
          rtoCost: 30,
          expectedPickup: "24 hours",
          expectedDelivery: "3-5 days",
          chargeableWeight: packageWeight,
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, [
    destinationPincode,
    heightInCm,
    lengthInCm,
    orderId,
    orderSetId,
    packageWeight,
    pickupPincode,
    price,
    resolvedMode,
    selectedPaymentMode,
    storeId,
    breadthInCm,
  ]);

  useEffect(() => {
    if (propProviders) {
      setProviders(propProviders);
      return;
    }

    if (isOpen && storeId) {
      fetchShippingProviders();
    }
  }, [isOpen, propProviders, storeId, fetchShippingProviders]);
  const handleSelect = (provider: ShippingProvider) => {
    onSelectProvider(provider);
    onClose();
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-3 w-3 ${
              star <= rating
                ? "fill-yellow-400 text-yellow-400"
                : "text-gray-300"
            }`}
          />
        ))}
        <span className="text-xs text-muted-foreground ml-1">{rating}</span>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto bg-card border border-border/40 shadow-2xl">
        <DialogHeader className="pb-4 border-b border-border/60">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-primary/10 rounded-md">
              <Truck className="h-4 w-4 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold text-foreground">
                Choose Shipping Provider
              </DialogTitle>
              <p className="text-xs text-muted-foreground">{modeLabel}</p>
            </div>
          </div>
        </DialogHeader>

        <div className="py-4">
          {resolvedMode === "legacy" && (
            <div className="px-4 mb-3">
              <label className="text-xs text-muted-foreground mr-2">
                Payment mode:
              </label>
              <select
                value={selectedPaymentMode}
                onChange={(e) =>
                  setSelectedPaymentMode(
                    e.target.value as "PREPAID" | "POSTPAID" | "SEMIPREPAID",
                  )
                }
                className="text-sm px-2 py-1 border rounded"
              >
                <option value="PREPAID">PREPAID</option>
                <option value="POSTPAID">POSTPAID</option>
                <option value="SEMIPREPAID">SEMIPREPAID</option>
              </select>
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <span className="ml-2 text-sm text-muted-foreground">
                Loading shipping providers...
              </span>
            </div>
          ) : providers.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <Package className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">
                  No shipping providers available
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-white/80 dark:bg-background rounded-lg border border-border/40 overflow-visible">
              <div className="bg-gray-50/80 dark:bg-muted/50 px-4 py-3 border-b border-border/40">
                <div className="grid grid-cols-7 gap-4 text-xs font-medium text-foreground">
                  <div>Partner Name</div>
                  <div>Rating</div>
                  <div>Delivery Charge</div>
                  <div>Shipment Type</div>
                  <div>RTO Cost</div>
                  <div>TAT (Days)</div>
                  <div>Action</div>
                </div>
              </div>

              <div className="divide-y divide-border/40">
                {providers.map((provider) => (
                  <div key={provider.id}>
                    <div className="grid grid-cols-1 md:grid-cols-7 gap-4 px-4 py-3 bg-white/80 dark:bg-background hover:bg-blue-50/60 dark:hover:bg-primary/20 transition-colors">
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-primary" />
                        <div>
                          <span className="text-sm font-medium text-foreground">
                            {provider.partnerName}
                          </span>
                          <div className="text-[10px] text-muted-foreground break-words">
                            {provider.serviceName
                              ? provider.serviceName + " • "
                              : ""}
                            {provider.id}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center">
                        {renderStars(provider.rating)}
                      </div>

                      <div className="flex items-center">
                        <span className="text-sm font-semibold text-foreground">
                          {formatCurrency(provider.deliveryCharge)}
                        </span>
                      </div>

                      <div className="flex items-center">
                        <Badge
                          variant="secondary"
                          className={`text-xs ${
                            provider.shipmentType === "AIR"
                              ? "bg-blue-500/20 text-blue-600"
                              : "bg-green-500/20 text-green-600"
                          }`}
                        >
                          {provider.shipmentType}
                        </Badge>
                      </div>

                      <div className="flex items-center">
                        <span className="text-sm text-foreground">
                          {formatCurrency(provider.rtoCost)}
                        </span>
                      </div>

                      <div className="flex items-center">
                        <span className="text-sm text-muted-foreground">
                          {provider.minTatInDays && provider.maxTatInDays
                            ? `${provider.minTatInDays}-${provider.maxTatInDays} days`
                            : provider.minTatInDays
                              ? `${provider.minTatInDays} days`
                              : provider.maxTatInDays
                                ? `${provider.maxTatInDays} days`
                                : "-"}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => handleSelect(provider)}
                          className="bg-primary text-primary-foreground hover:bg-primary/90 text-xs h-7 px-3 rounded-lg shadow-sm"
                        >
                          Choose
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => toggleDetails(provider.id)}
                          className="text-xs h-7 px-3 rounded-lg"
                        >
                          Details
                        </Button>
                      </div>
                    </div>

                    {expanded.has(provider.id) && (
                      <div className="px-6 py-3 bg-surface/50 border-t border-border/20 text-sm text-foreground">
                        <div className="grid grid-cols-2 gap-2">
                          <div className="text-xs text-muted-foreground">
                            Correlation ID
                          </div>
                          <div className="break-words">
                            {provider.correlationId || "-"}
                          </div>

                          <div className="text-xs text-muted-foreground">
                            Merchant ID
                          </div>
                          <div className="break-words">
                            {provider.merchantId || "-"}
                          </div>

                          <div className="text-xs text-muted-foreground">
                            Carrier Partner ID
                          </div>
                          <div className="break-words">
                            {provider.carrierPartnerId || "-"}
                          </div>

                          <div className="text-xs text-muted-foreground">
                            Carrier Public Name
                          </div>
                          <div className="break-words">
                            {provider.partnerName || "-"}
                          </div>

                          <div className="text-xs text-muted-foreground">
                            Zone ID
                          </div>
                          <div>{provider.zoneId || "-"}</div>

                          <div className="text-xs text-muted-foreground">
                            Zone Name
                          </div>
                          <div>{provider.zoneName || "-"}</div>

                          <div className="text-xs text-muted-foreground">
                            Chargeable Weight (kg)
                          </div>
                          <div>{provider.chargeableWeight ?? "-"}</div>

                          <div className="text-xs text-muted-foreground">
                            Dead Weight (g)
                          </div>
                          <div>
                            {provider.chargeableWeightDetails
                              ?.dead_weight_in_grams ?? "-"}
                          </div>

                          <div className="text-xs text-muted-foreground">
                            Volumetric Weight (g)
                          </div>
                          <div>
                            {provider.chargeableWeightDetails
                              ?.volumetric_weight_in_grams ?? "-"}
                          </div>

                          <div className="text-xs text-muted-foreground">
                            Chargeable Weight Category
                          </div>
                          <div>
                            {provider.chargeableWeightDetails
                              ?.chargeable_weight_category ?? "-"}
                          </div>

                          <div className="text-xs text-muted-foreground">
                            Order Type
                          </div>
                          <div>{provider.orderType || "-"}</div>

                          <div className="text-xs text-muted-foreground">
                            Shipment Order Type
                          </div>
                          <div>{provider.shipmentOrderType || "-"}</div>

                          <div className="text-xs text-muted-foreground">
                            Pickup Postal Code
                          </div>
                          <div>{provider.pickupPostalCode || "-"}</div>

                          <div className="text-xs text-muted-foreground">
                            Drop Postal Code
                          </div>
                          <div>{provider.dropPostalCode || "-"}</div>

                          <div className="text-xs text-muted-foreground">
                            Forward Base Price
                          </div>
                          <div>
                            {provider.forwardBasePrice
                              ? formatCurrency(provider.forwardBasePrice)
                              : "-"}
                          </div>

                          <div className="text-xs text-muted-foreground">
                            Fuel Surcharge %
                          </div>
                          <div>
                            {provider.fuelSurchargePercentageOfBasePrice ?? "-"}
                          </div>

                          <div className="text-xs text-muted-foreground">
                            Fuel Surcharge Amount
                          </div>
                          <div>
                            {provider.fuelSurchargeAmount
                              ? formatCurrency(provider.fuelSurchargeAmount)
                              : "-"}
                          </div>

                          <div className="text-xs text-muted-foreground">
                            RTO Charge %
                          </div>
                          <div>
                            {provider.rtoChargePercentOfForwardCharge ?? "-"}
                          </div>

                          <div className="text-xs text-muted-foreground">
                            RTO Cost
                          </div>
                          <div>
                            {provider.rtoCost
                              ? formatCurrency(provider.rtoCost)
                              : "-"}
                          </div>

                          <div className="text-xs text-muted-foreground">
                            Total Before Tax
                          </div>
                          <div>
                            {provider.totalAmountBeforeTax
                              ? formatCurrency(provider.totalAmountBeforeTax)
                              : "-"}
                          </div>

                          <div className="text-xs text-muted-foreground">
                            Tax %
                          </div>
                          <div>{provider.taxPercentage ?? "-"}</div>

                          <div className="text-xs text-muted-foreground">
                            Total Tax Amount
                          </div>
                          <div>
                            {provider.totalTaxAmount
                              ? formatCurrency(provider.totalTaxAmount)
                              : "-"}
                          </div>

                          <div className="text-xs text-muted-foreground">
                            Total After Tax
                          </div>
                          <div>
                            {provider.totalAmountAfterTax
                              ? formatCurrency(provider.totalAmountAfterTax)
                              : "-"}
                          </div>

                          <div className="text-xs text-muted-foreground">
                            Currency
                          </div>
                          <div>{provider.currencyCode || "-"}</div>

                          <div className="text-xs text-muted-foreground">
                            Min TAT (days)
                          </div>
                          <div>{provider.minTatInDays ?? "-"}</div>

                          <div className="text-xs text-muted-foreground">
                            Max TAT (days)
                          </div>
                          <div>{provider.maxTatInDays ?? "-"}</div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end pt-4 border-t border-border/60">
          <Button
            variant="outline"
            onClick={onClose}
            className="text-sm h-8 px-4 border-red-200 bg-red-50 text-red-600 hover:bg-red-100 hover:border-red-300 dark:border-red-800 dark:bg-red-950 dark:text-red-400 dark:hover:bg-red-900 dark:hover:border-red-700 rounded-lg"
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { ShippingProviderModal };
